"use client"

import React, { useEffect, useRef, useState } from "react"
import { Box, Typography } from "@mui/material"

const ProvidersMap = ({ locations = [], center }) => {
  const mapRef = useRef(null)
  const [map, setMap] = useState(null)
  const [markers, setMarkers] = useState([])
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)

  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsGoogleMapsLoaded(true)
    } else {
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${
        process.env.REACT_APP_GOOGLE_MAP_KEY || "YOUR_API_KEY"
      }&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => setIsGoogleMapsLoaded(true)
      script.onerror = () => console.error("Failed to load Google Maps")
      document.head.appendChild(script)
    }
  }, [])

  useEffect(() => {
    if (isGoogleMapsLoaded && mapRef.current && !map) {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: 12,
        center: center || { lat: 37.7749, lng: -122.4194 },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      })
      setMap(mapInstance)
    }
  }, [isGoogleMapsLoaded, center, map])

  useEffect(() => {
    if (map && window.google && window.google.maps) {
      // clear old markers
      markers.forEach((m) => m.setMap(null))

      const newMarkers = locations.map((loc) => {
        const marker = new window.google.maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          map,
          title: loc.name,
        })

        // Build Google Maps URL (lat/lng preferred; fallback to address)
        const mapsUrl = loc.lat && loc.lng
          ? `https://maps.google.com/?q=${loc.lat},${loc.lng}`
          : `https://maps.google.com/?q=${encodeURIComponent(
              `${loc.address || ""}, ${loc.city || ""}, ${loc.state || ""} ${loc.postalCode || ""}`
            )}`

        // Open Google Maps in a new tab on click
        marker.addListener("click", () => {
          window.open(mapsUrl, "_blank", "noopener,noreferrer")
        })

        // Keep info window on hover if desired
        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div><strong>${loc.name}</strong><br/>${loc.address || ""}</div>`,
        })
        marker.addListener("mouseover", () => infoWindow.open(map, marker))
        marker.addListener("mouseout", () => infoWindow.close())

        return marker
      })

      setMarkers(newMarkers)

      if (newMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds()
        newMarkers.forEach((m) => bounds.extend(m.getPosition()))
        map.fitBounds(bounds)
        if (newMarkers.length === 1) {
          map.setZoom(Math.min(map.getZoom(), 15))
        }
      }
    }
  }, [map, locations])

  if (!isGoogleMapsLoaded) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography>Loading map...</Typography>
      </Box>
    )
  }

  return <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
}

export default ProvidersMap
